
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  toRef,
  watch,
} from "vue";
import { IBulkDelete } from "@/interfaces/iPageModel";
import Datatable from "@/components/kt-datatable/KTDatatable.vue";
import { TranslationService } from "@/core/services/TranslationService";
import { INNER_PAGE_ROWS_PER_PAGE, usePagination } from "@/helpers/pagination";
import { FormatMethods } from "@/helpers/data-format";
import { LookupAPI } from "@/services/api/Lookup.service";
import { ILookup, ILookupLangModel } from "@/interfaces/iLookup";
import VZDeleteModal from "@/components/content/VZDeleteModal.vue";
import { hasPermission } from "@/core/services/permission.service";
import * as Yup from "yup";
import {
  lookupLangTableBulkDelete,
  lookupLangTableFilters,
  lookupLangTableHeaders,
} from "../dataTableOptions";
import { IAddEditFormModel } from "@/interfaces/iAddEditFormModel";

export default defineComponent({
  name: "lookup-lang",
  props: {
    dataProp: { type: Object as PropType<ILookup>, required: true },
    tableData: { type: Object as PropType<ILookupLangModel[]>, required: true },
    dataModelItem: {
      type: Object as any,
      required: true,
    },
  },
  components: { Datatable, VZDeleteModal },
  setup(props) {
    const t = (key: string) => {
      return TranslationService.translate(key);
    };
    const tableLookupLang = ref<null | HTMLFormElement>(null);
    const dataProp = toRef(props, "dataProp");
    const dataModelItemObj = toRef(props, "dataModelItem");
    const { page, total } = usePagination();
    const showDeleteDialog = ref<any>(false);
    const rowsPerPage = ref(INNER_PAGE_ROWS_PER_PAGE);
    const loading = ref(false);
    const defaultFilterLookupLang = ref({ status: "ACTIVE" });
    const showLookupLangSearch = true;
    const tableDataLookupLang = toRef(props, "tableData");
    let baseText = ref();
    const selectedForActionLookupLang = ref<Partial<ILookupLangModel>>({});
    const localPreference = ref({});
    const bulkDeleteLookupLang = ref<IBulkDelete>(lookupLangTableBulkDelete());
    const tableHeaderLookupLang = ref(lookupLangTableHeaders());
    const tableFilterLookupLang = ref(lookupLangTableFilters());
    const showLangTable = ref(true);
    const format = computed(() => {
      return FormatMethods;
    });

    const tableDataLookupLangObj = ref(
      computed(() => {
        return tableDataLookupLang.value;
      }).value
    );
    onMounted(() => {
      queryUpdateLookupLang(dataProp.value.webId, {
        ...defaultFilterLookupLang.value,
        page: page.value - 1,
        size: rowsPerPage.value,
      });
    });
    const syncQuery = async (params?: Record<string, number | string>) => {
      queryUpdateLookupLang(dataProp.value.webId, params);
    };
    let formDataLookupLang = ref<IAddEditFormModel>({
      header: "LookupLang",
      edit: false,
      data: null,
      label: [
        { addHeader: t("pages.lookup.addLanguage") },
        { editHeader: t("pages.lookup.editLanguage") },
      ],
      validationSchema: Yup.object({
        lang: Yup.string().required(
          t("pages.lookup.language") + " " + t("common.isRequired")
        ),
        text: Yup.string()
          .required(t("pages.lookup.text") + " " + t("common.isRequired"))
          .matches(/\S/, t("pages.lookup.text") + " " + t("common.isRequired")),
      }),
      model: [
        {
          name: "baseText",
          label: t("pages.lookup.baseText"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: true,
          value: dataModelItemObj.value.text,
        },
        {
          name: "lang",
          label: t("pages.lookup.language"),
          type: "list",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
          placeholder: "Please select language",
          options: [
            {
              label: t("English"),
              value: "ENGLISH",
            },
            {
              label: t("Spanish"),
              value: "SPANISH",
            },
            {
              label: t("French"),
              value: "FRENCH",
            },
          ],
        },
        {
          name: "text",
          label: t("pages.lookup.text"),
          type: "text",
          required: true,
          showOnlyInEdit: false,
          disabled: false,
        },
      ],
    });
    let permissionModel = ref<any>({
      create: "VYZOR:SETTINGS:LOOKUP CONFIGURATION:CREATE",
      delete: "VYZOR:SETTINGS:LOOKUP CONFIGURATION:DELETE",
      update: "VYZOR:SETTINGS:LOOKUP CONFIGURATION:UPDATE",
    });
    const editLookupLang = (item: ILookupLangModel) => {
      if (tableLookupLang.value) tableLookupLang.value.openAddEditModal = true;
      formDataLookupLang.value.data = { ...item };
      formDataLookupLang.value.edit = true;
    };
    const submitLookupLang = async (reqData) => {
      loading.value = true;
      reqData = {
        ...reqData,
        lookupDataId: dataModelItemObj.value.webId,
        status: "ACTIVE",
      };
      await LookupAPI.getInstance()
        .addLookupLang(reqData, formDataLookupLang.value.edit)
        .then(async () => {
          await refreshListLookupLang();
        })
        .catch(() => {
          //
        })
        .finally(() => {
          loading.value = false;
        });
    };
    const queryUpdateLookupLang = async (
      webId: number,
      params?: Record<string, number | string>
    ) => {
      try {
        loading.value = true;
        await LookupAPI.getInstance()
          .getLookupLangList(dataModelItemObj.value.webId, {
            ...params,
          })
          .then((data) => {
            tableDataLookupLangObj.value = [];
            localPreference.value = { ...params };
            tableDataLookupLangObj.value = [...data.content];
            total.value = data.totalElements;
            rowsPerPage.value = data.size;
            page.value = ++data.number;
          })
          .catch((err) => {
            console.error(err);
          });
      } catch (err) {
        console.error(err);
      } finally {
        loading.value = false;
      }
    };
    const refreshListLookupLang = async () => {
      showDeleteDialog.value = false;
      await queryUpdateLookupLang(dataModelItemObj.value.webId, {
        ...defaultFilterLookupLang.value,
        page: page.value - 1,
        size: rowsPerPage.value,
      });
    };

    watch(
      dataProp,
      (newValue) => {
        tableDataLookupLangObj.value = [];
        showLangTable.value = false;
        defaultFilterLookupLang.value = { ...defaultFilterLookupLang.value };
        queryUpdateLookupLang(+newValue, {
          ...defaultFilterLookupLang.value,
          page: page.value - 1,
          size: rowsPerPage.value,
        });
      },
      { deep: true }
    );
    watch(
      tableDataLookupLang,
      (newValue) => {
        tableDataLookupLangObj.value = [];
        tableDataLookupLangObj.value = newValue;
      },
      { deep: true }
    );
    return {
      bulkDeleteLookupLang,
      format,
      tableDataLookupLang,
      tableDataLookupLangObj,
      tableHeaderLookupLang,
      tableFilterLookupLang,
      showLookupLangSearch,
      page,
      total,
      rowsPerPage,
      defaultFilterLookupLang,
      loading,
      formDataLookupLang,
      editLookupLang,
      submitLookupLang,
      selectedForActionLookupLang,
      syncQuery,
      refreshListLookupLang,
      t,
      showLangTable,
      permissionModel,
      hasPermission,
      tableLookupLang,
      showDeleteDialog,
    };
  },
});
